// Config declarativa de las alertas (ALERT_TRIGGERED) del detalle de la bitácora.
// Un indicador = una entrada acá: qué campos consume, si su `level` es semántico, cómo se
// arma la frase-historia, qué mini-stats muestra y cómo se compone el título de la fila.
// El componente (renderers/AlertDetail.js) solo pinta lo que esta config describe: sumar
// un indicador nuevo NO toca JSX.
//
// Tres trampas del backend que esta config aísla:
//   - en 'goal' el `level` es la PRIORIDAD del objetivo ("1".."5"),
//   - en los ODS el `level` es el TIPO DE COMPARACIÓN (TYPE_ODS_VS_*),
//   - `value`/`expected_value`/`goal_value`/`vs_period_value` ya vienen FORMATEADOS
//     (con o sin moneda según la métrica) → passthrough, nunca re-formatear.
//
// ⚠️ SEGURIDAD — las frases `story` se inyectan con dangerouslySetInnerHTML (los templates
// llevan <strong>). Su seguridad depende de que la interpolación use el escapeValue POR
// DEFECTO de i18next (true): los valores del detail (nombres de cuenta/campaña, métricas,
// URLs) son controlables por terceros. PROHIBIDO pasar `interpolation: {escapeValue: false}`
// en los `values` de un story — ese idiom es solo para strings que se renderizan como TEXTO
// React (títulos, pies). Guardián: __tests__/storyEscaping.test.js (usa el i18n real).

import i18next from 'i18next';
import {isPlainObject} from './shapes';
import {
    humanizeFieldLabel, humanizePeriod, humanizeValue, formatAmount, formatMoney, formatNumber, formatPercent,
    formatDurationMinutes, isNumeric,
} from './humanize';
import {VALUE_LABELS, HIDDEN_FIELDS} from './labels';
import {truncateText} from './diff_field_types';

// Nombre legible de CADA indicador que puede disparar una alerta. Los que ya tienen key en
// VALUE_LABELS la reusan (incluidos los alias CriticOn*, que apuntan a la misma key que sus
// registros viejos AcosVsLastPeriod/RoasVsLastPeriod); el resto son keys nuevas.
export const INDICATOR_LABELS = {
    flex_indicator: VALUE_LABELS.flex_indicator,
    goal: VALUE_LABELS.goal,
    cost_vs_time: VALUE_LABELS.cost_vs_time,
    budget_exceeded: VALUE_LABELS.budget_exceeded,
    ods_critic: VALUE_LABELS.ods_critic,
    RoasVsLastPeriod: VALUE_LABELS.RoasVsLastPeriod,
    AcosVsLastPeriod: VALUE_LABELS.AcosVsLastPeriod,
    CriticOnROASVsLastPeriod: VALUE_LABELS.RoasVsLastPeriod,
    CriticOnACOSVsLastPeriod: VALUE_LABELS.AcosVsLastPeriod,
    ods_critic_budget_exhausted: 'activity_log.value.indicator_ods_budget_exhausted',
    // Sin título propio, la Bitácora usa la descripción del backend, que termina con el slug.
    ods_vs_platform_budget: 'activity_log.value.indicator_ods_vs_platform_budget',
    funds_are_critical: 'activity_log.value.indicator_funds_critical',
    no_impressions: 'activity_log.value.indicator_no_impressions',
    impressions_recovered: 'activity_log.value.indicator_impressions_recovered',
    budget_update_fail: 'activity_log.value.indicator_budget_update_fail',
    url_down: 'activity_log.value.indicator_url_down',
    url_recovered: 'activity_log.value.indicator_url_recovered',
    ad_account_disconnected: 'activity_log.value.indicator_ad_account_disconnected',
    ad_account_reconnected: 'activity_log.value.indicator_ad_account_reconnected',
    alert_rule: 'alert_indicators.alert_rule.label',
    alert_rule_recovered: 'alert_indicators.alert_rule_recovered.label',
};

// Tipo de ítem afectado. 'BUCC' no está en VALUE_LABELS y es la relación BU↔cuenta
// publicitaria: se muestra como cuenta publicitaria.
const ITEM_TYPE_LABELS = {
    CAMPAIGN: VALUE_LABELS.CAMPAIGN,
    ADGROUP: VALUE_LABELS.ADGROUP,
    ACCOUNT: VALUE_LABELS.ACCOUNT,
    AD_ACCOUNT: VALUE_LABELS.AD_ACCOUNT,
    BUCC: VALUE_LABELS.AD_ACCOUNT,
    BU: VALUE_LABELS.BU,
    BUSINESS_UNIT: VALUE_LABELS.BUSINESS_UNIT,
};

// El ítem cuyo nombre YA está en el badge de BU de la fila: repetirlo en el pie es ruido.
const ITEM_TYPES_IN_ROW_BADGE = new Set(['BU', 'BUSINESS_UNIT']);

// Campos comunes a toda alerta: los consume el marco (badge de nivel, pie de contexto),
// nunca son filas genéricas.
const BASE_FIELDS = ['indicator', 'level', 'item_type', 'item_label', 'item_identification'];

const TONE_BY_LEVEL = {CRITIC: 'critic', BAD: 'bad', GOOD: 'good'};

// Marcador donde va el <a> de la URL dentro de la frase ya traducida.
export const URL_TOKEN = '@@URL@@';

// ── Helpers de valores (todos devuelven null cuando el dato no está: un stat sin valor se
// omite, y "(vacío)" nunca es un valor válido en un stat) ──

function textOrNull(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') return value.trim() === '' ? null : value;
    return null;
}

// value/expected_value/goal_value/vs_period_value: passthrough del backend. Si
// excepcionalmente llega un número pelado se formatea como NÚMERO — nunca se le inventa
// un símbolo de moneda que la métrica puede no tener.
function preformatted(value) {
    const text = textOrNull(value);
    if (text !== null) return isInvalidMarker(text) ? null : text;
    return isNumeric(value) ? formatNumber(value) : null;
}

// `'---'` es el `INVALID_PERCENTAGE` / valor indefinido del backend (un CPA sin conversiones, por
// ejemplo): no es un dato y se omite, como cualquier otro ausente. Pintado como «Acumulado ---» se
// leía como un valor roto.
function isInvalidMarker(text) {
    return /^-{2,}$/.test(text.trim());
}

// Humaniza por campo (moneda, %, período, métrica) pero degrada a null en vez de "(vacío)".
function humanized(field, value, t) {
    if (value === null || value === undefined || value === '') return null;
    const result = humanizeValue(field, value, t);
    return result === null || result === '' ? null : result;
}

function percent(value) {
    return isNumeric(value) ? formatPercent(value) : null;
}

// Porcentaje ABSOLUTO y clampeado, sin signo ni símbolo: la dirección la pone la frase.
export function alertPct(value) {
    if (!isNumeric(value)) return null;
    return formatNumber(Math.min(Math.abs(Number(value)), 999));
}

// Delta con FLECHA (nunca solo color): "↓ 34%" / "↑ 24%". Con `direction` en `null` —no se sabe
// hacia dónde se movió— va sin flecha: "34%".
export function alertDelta(value, direction = signDirection(value)) {
    const pct = alertPct(value);
    if (pct === null) return null;
    if (direction !== 'UP' && direction !== 'DOWN') return `${pct}%`;
    return `${direction === 'DOWN' ? '↓' : '↑'} ${pct}%`;
}

function signDirection(value) {
    if (!isNumeric(value) || Number(value) === 0) return null;
    return Number(value) < 0 ? 'DOWN' : 'UP';
}

// Métricas cuya clase de alerta calcula el desvío en VALOR ABSOLUTO (`BudgetAlert` en el
// backend): ahí el signo de `fluctuation` no dice nada, y un costo que cayó a cero se leía «subió
// 100%». Espejo de `UNSIGNED_FLUCTUATION_METRICS` de `active_alerts_service.py`.
const UNSIGNED_FLUCTUATION_METRICS = new Set(['Cost']);

// Hacia dónde se movió el valor: 'UP', 'DOWN' o null (no se sabe → el texto no afirma dirección).
// Manda la que estampa el emisor (`fluctuation_direction`, desde el 23/09); sin ella, el signo
// vale sólo para las métricas cuya clase lo conserva.
export function fluctuationDirection(d) {
    if (d.fluctuation_direction === 'UP' || d.fluctuation_direction === 'DOWN') return d.fluctuation_direction;
    if (UNSIGNED_FLUCTUATION_METRICS.has(d.metric_name)) return null;
    return signDirection(d.fluctuation);
}

// La clave según la dirección: `OFF` (verbo neutro, «se desvió») cuando no se sabe. Las claves van
// escritas enteras y no armadas con un prefijo: el guardián `i18nKeys.test.js` las busca literales.
function directionKey(keys, d) {
    return keys[fluctuationDirection(d)] || keys.OFF;
}

const FLEX_STORY_KEYS = {
    DOWN: 'activity_log.detail.alert_flex_story_down',
    UP: 'activity_log.detail.alert_flex_story_up',
    OFF: 'activity_log.detail.alert_flex_story_off',
};
const FLEX_STORY_SHORT_KEYS = {
    DOWN: 'activity_log.detail.alert_flex_story_short_down',
    UP: 'activity_log.detail.alert_flex_story_short_up',
    OFF: 'activity_log.detail.alert_flex_story_short_off',
};
const FLEX_TITLE_KEYS = {
    DOWN: 'activity_log.title.alert_flex_down',
    UP: 'activity_log.title.alert_flex_up',
    OFF: 'activity_log.title.alert_flex_off',
};
const GOAL_TITLE_KEYS = {
    DOWN: 'activity_log.title.alert_goal_down',
    UP: 'activity_log.title.alert_goal_up',
    OFF: 'activity_log.title.alert_goal_off',
};

function stat(key, label, value, extra) {
    return {key, label, value, ...(extra || {})};
}

// ── Config por indicador ──
// story(detail, t) → {key, values} | {key, values, url} | null
// stats(detail, t, tone) → array de stats en orden jerárquico (desvío primero)
// bars(detail, t) → barras de progreso apiladas
// title(detail, t) → string | null (null ⇒ el registry cae a la description del backend)

// Los tres stats comparten UNA base temporal: lo que va del período principal. `vs_period_value`
// queda afuera a propósito — es el total CRUDO del período de comparación (los 7 días enteros),
// el insumo del que sale `expected_value`, no un par de los otros dos. Puesto al lado de un
// acumulado del mes se leía como una magnitud comparable y no lo es. De dónde sale el esperado
// se dice en el sub-label y en la frase-historia.
const flexStats = (d, t, tone) => [
    stat('deviation', t('activity_log.detail.stat_deviation'), alertDelta(d.fluctuation, fluctuationDirection(d)), {tone}),
    stat('value', t('activity_log.detail.stat_accumulated'), preformatted(d.value),
        {sub: humanized('main_period', d.main_period, t)}),
    stat('expected', t('activity_log.detail.stat_expected_today'), preformatted(d.expected_value),
        {sub: vsPeriodBasis(d, t)}),
    criticThresholdStat(d, t),
];

// Desde qué desvío la alerta pasa a crítica, tal como estaba configurado cuando sonó
// (`critic_threshold_pct`, en %). Sólo lo traen las alertas de flex y objetivos registradas
// desde 2026-09-23: las anteriores omiten el stat.
function criticThresholdStat(d, t) {
    const pct = alertPct(d.critic_threshold_pct);
    return stat('threshold', t('activity_log.detail.stat_threshold'), pct === null ? null : `${pct}%`);
}

// Hacia qué lado se despegó el gasto del tiempo, y cuántos puntos. Uno solo para el story y el
// título: si se calcularan por separado podrían contradecirse.
const costVsTimeDirection = d => {
    if (!isNumeric(d.budget_completion) || !isNumeric(d.time_completion)) return null;
    const budget = Number(d.budget_completion);
    const time = Number(d.time_completion);
    return {ahead: budget >= time, diff: formatNumber(Math.min(Math.abs(budget - time), 999))};
};

const vsPeriodBasis = (d, t) => {
    const period = humanized('vs_period', d.vs_period, t);
    return period ? t('activity_log.detail.stat_expected_basis', {period}) : null;
};

const impressionsStats = (d, t, tone) => [
    stat('value', t('activity_log.detail.stat_impressions_today'), preformatted(d.value), {tone}),
    stat('expected', t('activity_log.detail.stat_impressions_expected'), preformatted(d.expected_value)),
];

// `false` explícito: las alertas registradas antes del 05/10 no traen el dato y conservan el texto.
const watchesPartOfTheAccount = d => d.line_covers_whole_account === false;

const impressionsTitle = (d, t, key) => {
    const account = textOrNull(d.account_name);
    if (!account) return null;
    return t(key, {account, interpolation: {escapeValue: false}});
};

// El ODS y el gasto contra el que se compara son PLATA de la línea, pero el backend los guarda como
// `str(float)` («51.80386677816957»), sin formato. Se formatean con dos decimales y con la moneda de la
// línea si viene (`currency`, desde el 23/10); sin ella, sin símbolo: no se inventa una moneda. Un texto
// que no es número (una fila vieja ya formateada, el `'---'` del backend) sigue por `preformatted`.
function odsAmount(value, d) {
    if (!isNumeric(value)) return preformatted(value);
    const currency = textOrNull(d.currency);
    return currency ? formatMoney(value, currency) : formatAmount(value);
}

// Contra qué gasto se comparó el ODS: lo dice el `level`, que en el ODS es el TIPO DE COMPARACIÓN. Con un
// tipo desconocido queda el «Valor anterior» de siempre.
const ODS_SPEND_LABEL_KEYS = {
    TYPE_ODS_VS_TODAY: 'activity_log.detail.stat_ods_spend_today',
    TYPE_ODS_VS_YESTERDAY: 'activity_log.detail.stat_ods_spend_yesterday',
    TYPE_ODS_VS_LAST_7_DAYS: 'activity_log.detail.stat_ods_spend_last_7_days',
};

function odsSpendLabel(d, t) {
    const key = ODS_SPEND_LABEL_KEYS[d.level];
    return key ? t(key) : humanizeFieldLabel('previous_value', t);
}

const odsStats = (d, t, tone) => [
    stat('today_ods', humanizeFieldLabel('today_ods', t), odsAmount(d.today_ods, d), {tone}),
    stat('previous', odsSpendLabel(d, t), odsAmount(d.previous_value, d)),
];

// Lo que estampa `OdsVsPlatformBudget.detail_for_log` (backend): lo consumen los stats o no es para el usuario.
const ODS_VS_PLATFORM_FIELDS = ['ods', 'platform_daily', 'platforms', 'currency', 'total_budget', 'partial_goal',
    'end_date', 'direction', 'budget_exhausted', 'projected_end_date', 'leftover'];

// «12 de noviembre», en el idioma de la pantalla. El backend manda la fecha sin hora (`2026-11-12`): se lee como
// día local, no como medianoche UTC (que en Argentina sería el 11).
function longDay(isoDate) {
    const text = textOrNull(isoDate);
    if (!text) return null;
    const date = new Date(`${text.slice(0, 10)}T00:00:00`);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString(i18next.language || undefined, {day: 'numeric', month: 'long'});
}

// El título de la fila puesta al día, con la misma dirección que el aviso. Las claves van enteras: el guardián
// `i18nKeys.test.js` las busca literales.
const ODS_VS_PLATFORM_RESTATED_KEYS = {
    UP: 'activity_log.title.alert_ods_vs_platform_restated_up',
    DOWN: 'activity_log.title.alert_ods_vs_platform_restated_down',
    EXHAUSTED: 'activity_log.title.alert_ods_vs_platform_restated_exhausted',
    EXHAUSTED_PARTIAL: 'activity_log.title.alert_ods_vs_platform_restated_exhausted_partial',
};

function odsVsPlatformRestatedKey(d) {
    if (d.budget_exhausted === true) {
        return ODS_VS_PLATFORM_RESTATED_KEYS[d.partial_goal === true ? 'EXHAUSTED_PARTIAL' : 'EXHAUSTED'];
    }
    return ODS_VS_PLATFORM_RESTATED_KEYS[d.direction === 'DOWN' ? 'DOWN' : 'UP'];
}

// Primero lo cargado y después lo que necesita, como el título y el mail (/ux, 04/11). Con el presupuesto agotado,
// «necesita» no significa nada. El presupuesto se queda: es el número que la agencia cambia, y verlo prueba que
// el aviso tomó el cambio.
const odsVsPlatformStats = (d, t, tone) => [
    stat('platform_daily', t('activity_log.detail.stat_platform_daily'), odsAmount(d.platform_daily, d), {tone}),
    stat('ods', t('activity_log.detail.stat_line_needs_daily'),
        d.budget_exhausted === true ? null : odsAmount(d.ods, d)),
    stat('total_budget', t(d.partial_goal === true ? 'activity_log.detail.stat_partial_goal'
        : 'activity_log.detail.stat_budget'), odsAmount(d.total_budget, d)),
    textOrNull(d.projected_end_date)
        ? stat('projected_end_date', t('activity_log.detail.stat_runs_out_by'), longDay(d.projected_end_date))
        : stat('leftover', t('activity_log.detail.stat_left_unspent'), odsAmount(d.leftover, d)),
];

const vsLastPeriodConfig = metric => ({
    levelIsSemantic: true,
    fields: ['previous_value', 'new_value'],
    story: (d, t) => {
        const previous = preformatted(d.previous_value);
        const next = preformatted(d.new_value);
        if (!previous || !next) return null;
        return {key: 'activity_log.detail.alert_vs_last_period_story', values: {metric, previous, new: next}};
    },
    stats: (d, t, tone) => [
        stat('new', humanizeFieldLabel('new_value', t), preformatted(d.new_value), {tone}),
        stat('previous', humanizeFieldLabel('previous_value', t), preformatted(d.previous_value)),
    ],
    title: (d, t) => t('activity_log.title.alert_metric_worse', {metric, interpolation: {escapeValue: false}}),
    // Sin el ítem, «El ROAS empeoró vs. el período anterior» no decía en qué campaña: el label del
    // backend sí lo nombraba y el título nuevo lo perdió (QA de UX21-ALERTAS, 23/09).
    titleItemSuffix: true,
});

// Los slugs de producto son largos y `.entry-description` (el título de la fila) no tiene
// tope de ancho en CSS, así que el recorte va acá. Corta por la cola: el prefijo del path
// es lo que distingue una página de otra.
const URL_PATH_MAX_CHARS = 32;

// El estado de conexión de una cuenta publicitaria. `level` viaja en null (no hay severidad que
// mostrar: la cuenta conecta o no) y el ítem es la cuenta misma, así que el `item_identification`
// —el `CANAL||account_id` con el que se guarda su silenciado— queda como dato técnico y no se
// imprime (ya está en BASE_FIELDS).
const adAccountConnectionConfig = ({storyKey, titleKey}) => ({
    levelIsSemantic: false,
    fields: ['account_name', 'channel', 'account_id'],
    story: (d) => {
        // El nombre puede faltar (`AdAccount._name` es nullable): antes que no decir nada, el id.
        const account = textOrNull(d.account_name) || textOrNull(d.account_id);
        if (!account) return null;
        return {key: storyKey, values: {account}};
    },
    title: (d, t) => t(titleKey),
});

const urlConfig = ({storyKey, storyDurationKey, titleKey, titlePathKey, recovered = false}) => ({
    levelIsSemantic: false,
    // `previous_status` y `down_minutes` quedan DECLARADOS aunque no siempre se muestren: si no,
    // leftoverFields los imprimiría como filas genéricas ("Down minutes: 137").
    fields: ['url', 'previous_status', 'new_status', 'down_minutes'],
    story: (d, t) => {
        const url = textOrNull(d.url);
        const next = preformatted(d.new_status);
        if (!url || !next) return null;
        // Las filas anteriores al deploy del backend no traen la duración: frase corta.
        const duration = formatDurationMinutes(d.down_minutes, t);
        if (!duration) return {key: storyKey, values: {url: URL_TOKEN, new: next}, url};
        return {key: storyDurationKey, values: {url: URL_TOKEN, new: next, duration}, url};
    },
    stats: (d, t) => {
        const duration = formatDurationMinutes(d.down_minutes, t);
        // En la recuperación el estado anterior SÍ informa (404 → 200).
        if (recovered) {
            return [
                stat('new_status', humanizeFieldLabel('new_status', t), preformatted(d.new_status)),
                stat('previous_status', humanizeFieldLabel('previous_status', t), preformatted(d.previous_status)),
                stat('down_minutes', t('activity_log.detail.stat_outage_duration'), duration),
            ];
        }
        // En una caída, en cambio, el estado anterior es SIEMPRE el mismo error ("404 (antes
        // 404)"): el aviso sale recién al segundo chequeo caído, cuando el estado previo ya se
        // pisó. Lo que informa es hace cuánto no responde.
        return [
            stat('new_status', t('activity_log.detail.stat_http_status'), preformatted(d.new_status)),
            stat('down_minutes', t('activity_log.detail.stat_down_for'), duration),
        ];
    },
    title: (d, t) => {
        const parts = urlParts(d.url);
        if (!parts) return null;
        // escapeValue: false porque el título se renderiza como TEXTO React (no es un story);
        // sin esto las barras del path salen como &#x2F;.
        if (!parts.path) return t(titleKey, {host: parts.host, interpolation: {escapeValue: false}});
        return t(titlePathKey, {
            host: parts.host,
            path: truncateText(parts.path, URL_PATH_MAX_CHARS),
            interpolation: {escapeValue: false},
        });
    },
});

// ── Reglas personalizadas del Centro de Alertas (`alert_rule` / `alert_rule_recovered`) ──
// El detail lo escribe `AlertRuleEmitter.payload_for` del backend: `value` y `threshold` CRUDOS y en la
// misma escala (un PERCENTAGE en porcentaje humano: 30 = 30 %), `unit` NUMBER | PERCENTAGE | CURRENCY,
// `currency` sólo con CURRENCY (la de la línea), `operator` LT | LTE | GT | GTE y el `period` del backend.
const RULE_OPERATOR_KEYS = {
    LT: 'alerts_center.builder.op_lt',
    LTE: 'alerts_center.builder.op_lte',
    GT: 'alerts_center.builder.op_gt',
    GTE: 'alerts_center.builder.op_gte',
};

const CUSTOM_METRIC_ID = /^CUSTOM:\d+$/;

// Las del catálogo por su nombre traducido; una personalizada, por el `metric_label` que resolvió el
// backend (el cache de nombres del FE puede no haber llegado y diría «Métrica personalizada»).
function ruleMetric(d, t) {
    const label = textOrNull(d.metric_label);
    if (CUSTOM_METRIC_ID.test(d.metric || '') && label) return label;
    return humanized('metric_name', d.metric, t) || label;
}

// Sin el clamp de `formatPercent`: el umbral de una regla es el número que el usuario escribió.
function ruleValue(value, d) {
    if (!isNumeric(value)) return null;
    if (d.unit === 'PERCENTAGE') return `${formatNumber(value)}%`;
    if (d.unit === 'CURRENCY') return formatMoney(value, textOrNull(d.currency));
    return formatNumber(value);
}

function ruleOperator(d, t) {
    const key = RULE_OPERATOR_KEYS[d.operator];
    return key ? t(key) : null;
}

// Todo lo que lee la frase y lo técnico del envío: sin declararlo, `leftoverFields` lo imprimiría como
// filas genéricas (`rule_id`, los canales en crudo).
const RULE_FIELDS = ['rule_id', 'rule_name', 'metric', 'metric_label', 'operator', 'threshold', 'value', 'unit',
    'period', 'currency', 'channels', 'webhook_sent', 'recipients_count', 'rule_snoozed'];

// Los valores de la frase. Los de la story van SIN `escapeValue: false` (ver el aviso de arriba); el
// título se renderiza como texto y sí lo lleva.
function ruleParts(d, t) {
    const parts = {
        rule: textOrNull(d.rule_name),
        metric: ruleMetric(d, t),
        operator: ruleOperator(d, t),
        threshold: ruleValue(d.threshold, d),
        value: ruleValue(d.value, d),
        period: humanizePeriod(d.period, t) || null,
    };
    return parts.metric && parts.operator && parts.threshold ? parts : null;
}

const ruleStats = (d, t, tone) => [
    stat('value', t('activity_log.detail.stat_current_value'), ruleValue(d.value, d), {tone}),
    stat('threshold', t('activity_log.detail.stat_threshold'), ruleValue(d.threshold, d)),
];

const alertRuleConfig = ({titleKey, storyKey}) => ({
    levelIsSemantic: true,
    fields: RULE_FIELDS,
    story: (d, t) => {
        const parts = ruleParts(d, t);
        if (!parts || !parts.value || !parts.period) return null;
        const {metric, value, operator, threshold, period} = parts;
        return {key: storyKey, values: {metric, value, operator, threshold, period}};
    },
    stats: ruleStats,
    title: (d, t) => {
        const parts = ruleParts(d, t);
        if (!parts || !parts.rule) return null;
        const {rule, metric, operator, threshold} = parts;
        return t(titleKey, {rule, metric, operator, threshold, interpolation: {escapeValue: false}});
    },
});

export const ALERT_INDICATORS = {
    flex_indicator: {
        levelIsSemantic: true,
        fields: ['metric_name', 'main_period', 'vs_period', 'value', 'vs_period_value', 'expected_value', 'fluctuation',
            'critic_threshold_pct', 'fluctuation_direction'],
        story: (d, t) => {
            const metric = humanized('metric_name', d.metric_name, t);
            const pct = alertPct(d.fluctuation);
            if (!metric || pct === null) return null;
            const value = preformatted(d.value);
            const period = humanized('main_period', d.main_period, t);
            const vsPeriod = humanized('vs_period', d.vs_period, t);
            // Frase completa; si falta el contexto de períodos/valor, frase corta.
            if (!value || !period || !vsPeriod) {
                return {key: directionKey(FLEX_STORY_SHORT_KEYS, d), values: {metric, pct}};
            }
            return {
                key: directionKey(FLEX_STORY_KEYS, d),
                values: {metric, value, period, pct, vsPeriod},
            };
        },
        stats: flexStats,
        title: (d, t) => {
            const metric = humanized('metric_name', d.metric_name, t);
            const pct = alertPct(d.fluctuation);
            if (!metric || pct === null) return null;
            return t(directionKey(FLEX_TITLE_KEYS, d),
                {metric, pct, interpolation: {escapeValue: false}});
        },
        titleItemSuffix: true,
    },

    goal: {
        // El level es la PRIORIDAD del objetivo: jamás badge de nivel, jamás tone.
        levelIsSemantic: false,
        fields: ['metric_name', 'value', 'goal_value', 'expected_value', 'fluctuation', 'critic_threshold_pct',
            'fluctuation_direction'],
        story: (d, t) => {
            const metric = humanized('metric_name', d.metric_name, t);
            const value = preformatted(d.value);
            const goal = preformatted(d.goal_value);
            const expected = preformatted(d.expected_value);
            if (!metric || !value || !goal || !expected) return null;
            return {key: 'activity_log.detail.alert_goal_story', values: {metric, value, goal, expected}};
        },
        stats: (d, t) => [
            stat('deviation', t('activity_log.detail.stat_deviation'), alertDelta(d.fluctuation, fluctuationDirection(d))),
            stat('value', t('activity_log.detail.stat_current_value'), preformatted(d.value)),
            stat('goal', t('activity_log.detail.stat_goal'), preformatted(d.goal_value)),
            stat('expected', t('activity_log.detail.stat_expected_today'), preformatted(d.expected_value)),
            criticThresholdStat(d, t),
        ],
        priority: d => (d.level === null || d.level === undefined || d.level === '' ? null : String(d.level)),
        title: (d, t) => {
            const metric = humanized('metric_name', d.metric_name, t);
            const pct = alertPct(d.fluctuation);
            if (!metric || pct === null) return null;
            return t(directionKey(GOAL_TITLE_KEYS, d),
                {metric, pct, interpolation: {escapeValue: false}});
        },
        // Un objetivo puede ser de una campaña o un grupo: sin el ítem, el título no decía dónde.
        titleItemSuffix: true,
    },

    cost_vs_time: {
        levelIsSemantic: true,
        fields: ['total_spent', 'total_budget', 'time_completion', 'budget_completion'],
        story: (d, t) => {
            const direction = costVsTimeDirection(d);
            if (!direction) return null;
            return {
                key: direction.ahead
                    ? 'activity_log.detail.alert_cost_vs_time_story_ahead'
                    : 'activity_log.detail.alert_cost_vs_time_story_behind',
                values: {
                    budgetPct: alertPct(d.budget_completion),
                    timePct: alertPct(d.time_completion),
                    diff: direction.diff,
                },
            };
        },
        bars: (d, t, tone) => [
            {
                key: 'budget',
                label: t('activity_log.detail.progress_budget'),
                pct: d.budget_completion,
                text: percent(d.budget_completion),
                tone,
            },
            {
                key: 'time',
                label: t('activity_log.detail.progress_time'),
                pct: d.time_completion,
                text: percent(d.time_completion),
                tone: 'time',
            },
        ],
        stats: (d, t) => [
            stat('spent', t('activity_log.detail.stat_spent'), humanized('total_spent', d.total_spent, t)),
            stat('budget', t('activity_log.detail.stat_total_budget'), humanized('total_budget', d.total_budget, t)),
        ],
        title: (d, t) => {
            // La alerta es BIDIRECCIONAL (el backend evalúa el desvío en valor absoluto), así
            // que el título tiene que decir para qué lado. Con un título fijo "se consume más
            // rápido" contradecía sus propios números cada vez que el gasto iba atrasado.
            const direction = costVsTimeDirection(d);
            if (!direction) return t('activity_log.title.alert_cost_vs_time');
            return t(direction.ahead
                ? 'activity_log.title.alert_cost_vs_time_ahead'
                : 'activity_log.title.alert_cost_vs_time_behind', {diff: direction.diff});
        },
    },

    budget_exceeded: {
        levelIsSemantic: true,
        fields: ['cost', 'budget', 'alert_when_spent_over', 'completion'],
        story: (d, t) => {
            const cost = humanized('cost', d.cost, t);
            const threshold = alertPct(d.alert_when_spent_over);
            const budget = humanized('budget', d.budget, t);
            if (!cost || threshold === null || !budget) return null;
            return {key: 'activity_log.detail.alert_budget_exceeded_story', values: {cost, threshold, budget}};
        },
        stats: (d, t, tone) => [
            stat('completion', t('activity_log.detail.stat_progress'), percent(d.completion), {tone}),
            stat('elapsed', t('activity_log.detail.stat_period_elapsed'), percent(d.period_elapsed_pct)),
            stat('cost', t('activity_log.detail.stat_consumption'), humanized('cost', d.cost, t)),
            stat('budget', t('activity_log.detail.stat_budget'), humanized('budget', d.budget, t)),
            stat('threshold', t('activity_log.detail.stat_threshold'), percent(d.alert_when_spent_over)),
        ],
        // Con el período transcurrido el título dice si va adelantada o atrasada: «72 % con el 97 %
        // del período» es ir atrasado, y sin ese dato se leyó como un exceso (card Wq8SqQV9). Las
        // alertas viejas no lo traen y conservan el título de siempre.
        title: (d, t) => {
            const pct = alertPct(d.completion);
            if (pct === null) return null;
            const elapsed = alertPct(d.period_elapsed_pct);
            return elapsed === null
                ? t('activity_log.title.alert_budget_exceeded', {pct})
                : t('activity_log.title.alert_budget_exceeded_with_elapsed', {pct, elapsed});
        },
    },

    funds_are_critical: {
        levelIsSemantic: true,
        fields: ['remaining_days', 'available_funds'],
        story: (d, t) => {
            const funds = humanized('available_funds', d.available_funds, t);
            const days = isNumeric(d.remaining_days) ? formatNumber(d.remaining_days) : null;
            if (!funds || days === null) return null;
            return {key: 'activity_log.detail.alert_funds_story', values: {funds, days}};
        },
        // Estas alertas son SIEMPRE críticas: el desvío va en rojo aunque falte el level.
        stats: (d, t) => [
            stat('remaining_days', t('activity_log.detail.stat_remaining_days'),
                isNumeric(d.remaining_days) ? formatNumber(d.remaining_days) : null, {tone: 'critic'}),
            stat('available_funds', t('activity_log.detail.stat_available_funds'),
                humanized('available_funds', d.available_funds, t)),
        ],
        title: (d, t) => {
            const days = isNumeric(d.remaining_days) ? formatNumber(d.remaining_days) : null;
            return days === null ? null : t('activity_log.title.alert_funds', {days});
        },
    },

    no_impressions: {
        levelIsSemantic: true,
        // `channel` se consume a propósito: hoy no hay mapa de labels de canal y mostrarlo
        // imprimiría el enum crudo (GOOGLE_CHANNEL). El nombre de la cuenta ya lo sugiere.
        fields: ['account_name', 'channel', 'value', 'expected_value', 'account_impressions_today'],
        story: (d, t) => {
            const account = textOrNull(d.account_name);
            const value = preformatted(d.value);
            const expected = preformatted(d.expected_value);
            if (!account || value === null || expected === null) return null;
            const accountValue = preformatted(d.account_impressions_today);
            if (watchesPartOfTheAccount(d) && accountValue !== null) {
                return {key: 'activity_log.detail.alert_no_impressions_line_story',
                    values: {account, value, expected, account_value: accountValue}};
            }
            return {key: 'activity_log.detail.alert_no_impressions_story', values: {account, value, expected}};
        },
        stats: (d, t, tone) => [
            ...impressionsStats(d, t, tone),
            stat('account', t('activity_log.detail.stat_impressions_account_today'),
                preformatted(d.account_impressions_today)),
        ],
        // La alerta mide las campañas de la LÍNEA. Cuando la línea mira sólo algunas, decir «la
        // cuenta dejó de registrar impresiones» con la cuenta imprimiendo en otras se leyó como una
        // alerta falsa (MILLIE, card xcUa4a3N).
        title: (d, t) => impressionsTitle(d, t, watchesPartOfTheAccount(d)
            ? 'activity_log.title.alert_no_impressions_line'
            : 'activity_log.title.alert_no_impressions'),
    },

    impressions_recovered: {
        levelIsSemantic: true,
        fields: ['account_name', 'channel', 'value', 'expected_value'],
        story: (d, t) => {
            const account = textOrNull(d.account_name);
            const value = preformatted(d.value);
            if (!account || value === null) return null;
            return {key: 'activity_log.detail.alert_impressions_recovered_story', values: {account, value}};
        },
        stats: impressionsStats,
        title: (d, t) => impressionsTitle(d, t, 'activity_log.title.alert_impressions_recovered'),
    },

    ods_critic: {
        // El level es el TIPO DE COMPARACIÓN (TYPE_ODS_VS_*), no un nivel de alerta.
        levelIsSemantic: false,
        // Pero la alerta SIEMPRE es crítica: el backend la escribe sólo al ENTRAR en CRITIC
        // (`ODSManager._notify_if_alert_is_critic_and_was_not`, y en el Centro v2 la regla de sistema se
        // declara con `MetricAlertLevel.CRITIC`). Espejo: `CRITIC_ON_EMISSION_INDICATORS` del backend.
        emittedLevel: 'CRITIC',
        fields: ['today_ods', 'previous_value', 'currency'],
        story: (d, t) => {
            const ods = odsAmount(d.today_ods, d);
            const previous = odsAmount(d.previous_value, d);
            if (ods === null || previous === null) return null;
            const comparisonKey = VALUE_LABELS[d.level];
            if (!comparisonKey) {
                return {key: 'activity_log.detail.alert_ods_story_plain', values: {ods, previous}};
            }
            return {key: 'activity_log.detail.alert_ods_story', values: {ods, previous, comparison: t(comparisonKey)}};
        },
        stats: odsStats,
        title: (d, t) => t('activity_log.title.alert_ods'),
    },

    ods_critic_budget_exhausted: {
        // La variante de `ods_critic` con el presupuesto agotado: mismo camino de emisión, mismo nivel.
        levelIsSemantic: false,
        emittedLevel: 'CRITIC',
        fields: ['today_ods', 'previous_value', 'currency'],
        story: (d, t) => {
            const ods = odsAmount(d.today_ods, d);
            if (ods === null) return null;
            return {key: 'activity_log.detail.alert_ods_exhausted_story', values: {ods}};
        },
        stats: odsStats,
        title: (d, t) => t('activity_log.title.alert_ods_exhausted'),
    },

    // «Las campañas tienen cargado más (o menos) por día de lo que necesita la línea»: mismo manager que el ODS, y
    // también se escribe sólo al entrar en crítico (`ODSManager._notify_ods_vs_platform_budget`); su `level` es
    // `TYPE_ODS_VS_PLATFORM_BUDGET` (review C-fix-ods-01, fede 23/10). Sin título propio: el de la fila es el
    // `label` del backend, con la línea como se llama hoy y la dirección. Los datos, en el orden del mail (/ux,
    // 04/11): sin ellos, Atómica no podía ver que el aviso era del presupuesto de antes (card Fnvibexi).
    ods_vs_platform_budget: {
        levelIsSemantic: false,
        emittedLevel: 'CRITIC',
        fields: ODS_VS_PLATFORM_FIELDS,
        stats: odsVsPlatformStats,
        // El aviso que el backend vuelve a registrar con los números de después de un cambio guardado no volvió a
        // sonar (/ux, 04/11): en la Bitácora su fila lo dice. Un disparo devuelve `null` y sigue con la descripción.
        // En la bandeja no: ahí el título es el `label` del backend, con la línea (`titleOnlyInLog`).
        title: (d, t) => (d.restated_after_change === true ? t(odsVsPlatformRestatedKey(d)) : null),
        titleOnlyInLog: true,
    },
    // Su recuperación (card Fnvibexi): sólo va a la Bitácora, nunca a la bandeja.
    ods_vs_platform_budget_recovered: {
        levelIsSemantic: false,
        fields: ODS_VS_PLATFORM_FIELDS,
        stats: (d, t) => [
            stat('platform_daily', t('activity_log.detail.stat_platform_daily'), odsAmount(d.platform_daily, d)),
            stat('ods', t('activity_log.detail.stat_line_needs_daily'), odsAmount(d.ods, d)),
        ],
        title: (d, t) => t('activity_log.title.alert_ods_vs_platform_recovered'),
    },
    // El gasto del día volvió a lo esperado (la recuperación de `ods_critic`, con su comparación en `level`).
    ods_recovered: {
        levelIsSemantic: false,
        fields: ['today_ods', 'previous_value', 'currency'],
        stats: (d, t) => odsStats(d, t, null),
        title: (d, t) => t('activity_log.title.alert_ods_recovered'),
    },

    CriticOnROASVsLastPeriod: vsLastPeriodConfig('ROAS'),
    CriticOnACOSVsLastPeriod: vsLastPeriodConfig('ACOS'),
    // Registros viejos, mismo shape.
    RoasVsLastPeriod: vsLastPeriodConfig('ROAS'),
    AcosVsLastPeriod: vsLastPeriodConfig('ACOS'),

    url_down: urlConfig({
        storyKey: 'activity_log.detail.alert_url_down_story_short',
        storyDurationKey: 'activity_log.detail.alert_url_down_story',
        titleKey: 'activity_log.title.alert_url_down',
        titlePathKey: 'activity_log.title.alert_url_down_path',
    }),
    url_recovered: urlConfig({
        storyKey: 'activity_log.detail.alert_url_recovered_story_short',
        storyDurationKey: 'activity_log.detail.alert_url_recovered_story',
        titleKey: 'activity_log.title.alert_url_recovered',
        titlePathKey: 'activity_log.title.alert_url_recovered_path',
        recovered: true,
    }),

    // Sin frase: `is_temporary` como fila genérica alcanza (business_unit_id es técnico).
    budget_update_fail: {levelIsSemantic: false, fields: []},

    ad_account_disconnected: adAccountConnectionConfig({
        storyKey: 'activity_log.detail.alert_ad_account_disconnected_story',
        titleKey: 'activity_log.title.alert_ad_account_disconnected',
    }),
    ad_account_reconnected: adAccountConnectionConfig({
        storyKey: 'activity_log.detail.alert_ad_account_reconnected_story',
        titleKey: 'activity_log.title.alert_ad_account_reconnected',
    }),

    // Las reglas del Centro de Alertas: el título nombra la regla y su condición, como «Mis reglas».
    alert_rule: alertRuleConfig({
        titleKey: 'alert_indicators.alert_rule.title',
        storyKey: 'alert_indicators.alert_rule.story',
    }),
    alert_rule_recovered: alertRuleConfig({
        titleKey: 'alert_indicators.alert_rule_recovered.title',
        storyKey: 'alert_indicators.alert_rule_recovered.story',
    }),
};

export function getIndicatorConfig(indicator) {
    return (typeof indicator === 'string' && ALERT_INDICATORS[indicator]) || null;
}

export function indicatorLabelKey(indicator) {
    return (typeof indicator === 'string' && INDICATOR_LABELS[indicator]) || null;
}

// Level que puede ir al badge: solo si el indicador lo usa como NIVEL. Un indicador
// desconocido se asume semántico (LevelBadge ya filtra lo que no es CRITIC/BAD/NORMAL/GOOD).
export function badgeLevel(detail) {
    if (!isPlainObject(detail)) return null;
    const config = getIndicatorConfig(detail.indicator);
    if (config && config.levelIsSemantic === false) return null;
    return detail.level || null;
}

// La gravedad con la que se muestra la alerta: el level cuando es semántico y, si no, el nivel con el
// que el indicador se emite siempre (`emittedLevel`: el ODS sólo se registra al entrar en crítico). Una
// regla explícita por indicador, nunca leer un level técnico como gravedad: la prioridad «2» de un
// objetivo no es BAD.
export function alertSeverityLevel(detail) {
    const level = badgeLevel(detail);
    if (level) return level;
    const config = isPlainObject(detail) ? getIndicatorConfig(detail.indicator) : null;
    return (config && config.emittedLevel) || null;
}

export function alertTone(detail) {
    return TONE_BY_LEVEL[alertSeverityLevel(detail)] || null;
}

const ITEM_SEVERITY_LEVELS = new Set(['CRITIC', 'BAD']);

// Lo mismo para un ítem de `active-alerts` (el Centro, el popover, la pantalla de Alertas del cliente):
// el `level` del ítem si es una gravedad y, si no, el `emittedLevel` de su indicador. Es la que pinta la
// fila Y la que cuenta en «Críticas», así que las dos no pueden decir cosas distintas.
export function itemSeverityLevel(item) {
    if (!item) return null;
    if (ITEM_SEVERITY_LEVELS.has(item.level)) return item.level;
    const config = getIndicatorConfig(item.indicator);
    return (config && config.emittedLevel) || null;
}

// Ítem afectado para el pie. Se omite cuando es la BU: su nombre ya está en el badge de la fila.
export function affectedItem(detail, t) {
    if (!isPlainObject(detail)) return null;
    const name = textOrNull(detail.item_label);
    if (!name) return null;
    const type = detail.item_type;
    if (ITEM_TYPES_IN_ROW_BADGE.has(type)) return null;
    const typeKey = ITEM_TYPE_LABELS[type];
    return {type: typeKey ? t(typeKey) : null, name};
}

// Campos que ningún indicador declaró: van como filas label/valor al final en vez de
// desaparecer sin dejar rastro.
export function leftoverFields(detail, config) {
    if (!isPlainObject(detail)) return [];
    const consumed = new Set(BASE_FIELDS);
    ((config && config.fields) || []).forEach(field => consumed.add(field));
    return Object.entries(detail).filter(([field, value]) => !consumed.has(field)
        && !HIDDEN_FIELDS.has(field)
        && value !== null && value !== undefined && value !== '');
}

export function isSafeHttpUrl(url) {
    return typeof url === 'string' && /^https?:\/\//i.test(url.trim());
}

// Host y path de la URL alertada. El path es lo que distingue una página de otra: sin él,
// N páginas rotas del mismo dominio dan N títulos idénticos (y el colapso de la bitácora
// agrupa por URL completa, así que tampoco se funden en una fila).
// El doble slash de `//212-vip-black` NO se normaliza: es el dato, y delata un final URL
// mal armado en el anuncio. El hash no llega al servidor, se ignora.
export function urlParts(url) {
    if (!isSafeHttpUrl(url)) return null;
    try {
        const parsed = new URL(url.trim());
        const host = parsed.hostname || null;
        if (!host) return null;
        const path = `${parsed.pathname}${parsed.search}`;
        return {host, path: path && path !== '/' ? path : null};
    } catch (e) {
        return null;
    }
}

// Parte la frase ya traducida en el marcador de la URL. Si el marcador no está (traducción
// vieja o t identidad en tests), la URL se antepone y la frase se conserva completa.
export function splitOnToken(text, token) {
    const value = typeof text === 'string' ? text : '';
    const index = value.indexOf(token);
    if (index === -1) return ['', value];
    return [value.slice(0, index), value.slice(index + token.length)];
}

// Título enriquecido de la FILA (reemplaza "Alerta activada en 'X': flex_indicator").
// Devuelve null si el indicador no está en la config o faltan los datos para componerlo:
// el registry cae a la description del backend, así que nunca queda peor que hoy.
// `inTray`: la bandeja de alertas vigentes, que con `titleOnlyInLog` usa el `label` del backend.
export function alertTitle(entry, t, {inTray = false} = {}) {
    const detail = entry && entry.detail;
    if (!isPlainObject(detail)) return null;
    const config = getIndicatorConfig(detail.indicator);
    if (!config || !config.title || (inTray && config.titleOnlyInLog)) return null;
    let title;
    try {
        title = config.title(detail, t);
    } catch (e) {
        return null;
    }
    if (!title) return null;
    if (!config.titleItemSuffix) return title;
    const item = affectedItem(detail, t);
    if (!item) return title;
    // Con el TIPO cuando se sabe: una campaña y un grupo homónimos daban dos títulos idénticos
    // («CTR cayó 100% … en 'Paracas - Fishing'») y no había forma de saber cuál era cuál.
    return t(item.type ? 'activity_log.title.alert_in_typed_item' : 'activity_log.title.alert_in_item', {
        title,
        item: item.name,
        type: item.type,
        interpolation: {escapeValue: false},
    });
}
